import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, ClipboardCheck, Pause, Play, Sun, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, LoadingState, ErrorState, EmptyState, StatusBadge } from "@/components/common";
import { useGarage } from "@/context/GarageContext";
import { api, errMsg } from "@/lib/api";
import { t } from "@/i18n";

export const hm = (mins) => `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m`;
export const clock = (iso) => {
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  return `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

function useTick(on) {
  const [, set] = useState(0);
  useEffect(() => { if (!on) return undefined; const id = setInterval(() => set((x) => x + 1), 1000); return () => clearInterval(id); }, [on]);
}

function DayCard({ j, reload, readOnly }) {
  const navigate = useNavigate();
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const photo = (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("phase", ["ready", "in_progress"].includes(j.status) ? "after" : "before");
    act(() => api.post(`/jobs/${j.id}/photos`, fd), t("jobs.photoAdded")).then(() => { ref.current.value = ""; });
  };
  const nextStep = j.next_statuses.includes("ready") ? "ready" : j.next_statuses.find((s) => !["cancelled", "awaiting_approval", "inspecting"].includes(s) || j.status === "received");
  const big = "h-14 flex-col gap-1 text-xs font-semibold";
  return (
    <div className={`gf-card overflow-hidden ${j.running ? "ring-2 ring-teal-500" : ""}`} data-testid={`myday-card-${j.number}`}>
      <button type="button" onClick={() => navigate(`/pro/jobs/${j.id}`)} className="block w-full p-4 text-left" data-testid={`myday-open-${j.number}`}>
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-xs font-semibold text-teal-700">{j.number}</span>
          <StatusBadge status={j.status} />
        </div>
        <p className="mt-2 flex items-center gap-2">
          <span className="rounded border-2 border-slate-900 bg-yellow-300 px-1.5 font-mono-plate text-sm font-bold">{j.vehicle.plate}</span>
          <span className="truncate font-semibold text-slate-900">{j.vehicle.make} {j.vehicle.model}</span>
        </p>
        <p className="mt-1 truncate text-sm text-slate-500">{j.customer.name}</p>
        {j.reason && <p className="mt-2 line-clamp-2 text-sm text-slate-700">{j.reason}</p>}
      </button>
      <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50 px-4 py-2 text-xs">
        <span className="text-slate-500">{t("b12.day.mine")}: <strong className="text-slate-800">{hm(j.my_minutes_today)}</strong></span>
        {j.running && <span className="font-mono text-base font-bold text-teal-700" data-testid={`myday-clock-${j.number}`}>{clock(j.running.started_at)}</span>}
      </div>
      {!readOnly && (
        <div className="grid grid-cols-4 gap-1 p-2">
          <Button variant={j.running ? "default" : "outline"} className={big} disabled={busy} data-testid={`myday-timer-${j.number}`}
            onClick={() => act(() => api.post(`/jobs/${j.id}/timer/${j.running ? "stop" : "start"}`))}>
            {j.running ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}{j.running ? t("b12.day.pause") : t("b12.day.start")}
          </Button>
          <Button variant="outline" className={big} disabled={busy} onClick={() => ref.current.click()} data-testid={`myday-photo-${j.number}`}><Camera className="h-5 w-5" />{t("b12.day.photo")}</Button>
          <Button variant="outline" className={big} onClick={() => navigate(`/pro/jobs/${j.id}#inspection`)} data-testid={`myday-inspect-${j.number}`}><ClipboardCheck className="h-5 w-5" />{t("b12.day.inspect")}</Button>
          <Button variant="outline" className={`${big} ${nextStep === "ready" ? "border-emerald-500 text-emerald-700" : ""}`} disabled={busy || !nextStep} data-testid={`myday-next-${j.number}`}
            onClick={() => act(() => api.post(`/jobs/${j.id}/status`, { status: nextStep }), t("jobs.statusChanged", { status: t(`status.${nextStep}`) }))}>
            <CheckCircle2 className="h-5 w-5" /><span className="truncate">{nextStep === "ready" ? t("b12.day.ready") : nextStep ? t(`status.${nextStep}`) : "—"}</span>
          </Button>
          <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => photo(e.target.files[0])} data-testid={`myday-photo-input-${j.number}`} />
        </div>
      )}
    </div>
  );
}

export default function MyDay() {
  const { readOnly } = useGarage();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => api.get("/my-day").then((r) => { setData(r.data); setError(""); }).catch((e) => setError(errMsg(e))), []);
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);
  useTick(!!data?.jobs.some((j) => j.running));
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <LoadingState />;
  return (
    <div data-testid="my-day">
      <PageHeader eyebrow={new Intl.DateTimeFormat("en-AU", { weekday: "long", day: "numeric", month: "long" }).format(new Date())} title={t("b12.day.title")}
        subtitle={t("b12.day.total", { time: hm(data.total_minutes_today) })} />
      {data.jobs.length === 0 ? <EmptyState icon={Sun} title={t("b12.day.empty")} text={t("b12.day.emptyText")} testId="myday-empty" /> : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">{data.jobs.map((j) => <DayCard key={j.id} j={j} reload={load} readOnly={readOnly} />)}</div>
      )}
    </div>
  );
}
